"use client";

import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Typography,
} from "@repo/ui";
import { CheckCircleIcon } from "@spoctech/icons";
import { load } from "cheerio";
import Link from "next/link";
import { useMemo } from "react";

export type ServiceContentItem = {
  heading: string;
  sub_heading: string;
  is_html_description: boolean;
  link: string;
  image: string;
  description: string;
};

export type ServiceContents = {
  description: ServiceContentItem;
  advantages: ServiceContentItem;
  interestingFact: ServiceContentItem;
};

export type ConsultingCardProps = {
  id: number;
  slug: string;
  name: string;
  service_image: string;
  service_contents: ServiceContents;
};

export default function ConsultingCard({
  name,
  service_image,
  slug,
  service_contents,
}: ConsultingCardProps) {
  const advantages = useMemo((): string | string[] => {
    if (service_contents.advantages.is_html_description) {
      const $ = load(service_contents.advantages.description);
      const items: string[] = [];
      $("ul li").each(function () {
        items.push($(this).text());
      });
      return items.length > 0 ? items : service_contents.advantages.description;
    }
    return service_contents.advantages.description;
  }, [service_contents.advantages]);

  if (!name) return null;

  return (
    <Card className="relative h-full">
      <CardHeader className="relative !p-0">
        <Box className="absolute text-center left-0 right-0 top-[50%] -translate-y-1/2">
          <Typography
            variant="h2"
            className="!font-semi-bold !text-2xl !text-white"
          >
            {name}
          </Typography>
        </Box>

        <CardMedia
          className="w-full max-h-[187px] h-auto"
          src={service_image}
        />
      </CardHeader>

      <CardContent className="flex flex-col min-h-[450px]">
        <Typography paragraph className="!text-gray-500 flex-grow">
          {service_contents?.description?.description}
        </Typography>

        <Box className="flex-grow">
          <Typography
            paragraph
            className="!font-bold !text-base !text-neutral-700"
          >
            Advantages
          </Typography>

          {Array.isArray(advantages) ? (
            <ul className="columns-1 sm:columns-2 mb-4">
              {advantages.map((item, index) => (
                <li
                  key={`advantages-${index}`}
                  className="inline-flex gap-1 mb-4 last:mb-0"
                >
                  <CheckCircleIcon className="!text-primary-light flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Typography>{advantages}</Typography>
          )}
        </Box>
        <Box className="!bg-slate-100 !rounded-2xl !p-4 mb-4">
          <Typography
            paragraph
            className="!font-bold !text-base !text-neutral-700"
          >
            Interesting Fact
          </Typography>
          <Typography paragraph className="!text-neutral-600 !text-sm">
            {service_contents?.interestingFact?.description}
          </Typography>
        </Box>
      </CardContent>
      <CardActions>
        <Button
          color="primary"
          label={`View ${name}`}
          size="medium"
          variant="outlined"
          fullWidth
          render={<Link href={slug} />}
        />
      </CardActions>
    </Card>
  );
}
